.card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: var(--sp-4);
  padding: var(--sp-4) var(--sp-5);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
  cursor: grab;
  box-shadow: var(--glass-rim);
  transition:
    transform var(--dnd-dur) var(--dnd-ease),
    box-shadow var(--dnd-dur) var(--dnd-ease);
}

.card:hover {
  border-color: var(--border-strong);
}

.card.lifted,
.card:active {
  cursor: grabbing;
}

.card.lifted {
  transform: scale(1.02);
  box-shadow: var(--glass-rim), var(--shadow-lift);
  touch-action: none;
  position: relative;
  z-index: 5;
}

.marker {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--accent, var(--border-strong));
  color: var(--accent, var(--text-2));
}

.body {
  flex: 1 1 auto;
  min-width: 0;
}

.row1 {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-4);
}

.iataTitle {
  font-size: var(--fs-lg);
}

.title {
  font-size: var(--fs-md);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price {
  flex: none;
  font-size: var(--fs-md);
  font-weight: 600;
  color: var(--text);
}

.pairNote {
  flex: none;
  font-size: var(--fs-xs);
  color: var(--text-3);
}

.times {
  display: flex;
  align-items: baseline;
  gap: var(--sp-4);
  margin-top: var(--sp-1);
  font-size: var(--fs-sm);
  color: var(--text-2);
}

.dur {
  color: var(--text-3);
}

.subtitle {
  display: flex;
  align-items: baseline;
  gap: var(--sp-3);
  margin-top: var(--sp-1);
  font-size: var(--fs-sm);
  color: var(--text-2);
}

/* Строка бейджа цены. Своя строка нужна и на широком экране: бейдж несёт
   название сайта и сумму, в ряд с временем он не встаёт.
   min-width: 0 — чтобы длинное название агрегатора ужималось многоточием, а
   не распирало карточку; :empty гасит строку, когда проверка цен недоступна
   и PriceBadge отдал null. */
.priceLine {
  display: flex;
  min-width: 0;
  margin-top: var(--sp-1);
}

.priceLine:empty {
  display: none;
}

.priceLine > * {
  min-width: 0;
}

/* Ссылка на источник цены. Карточку таскают за любое место, поэтому ссылка
   должна быть заметна как ссылка, а не как часть подписи. */
.sourceLink {
  color: var(--flight);
  font-size: var(--fs-xs);
  white-space: nowrap;
}

.warnings {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  margin: var(--sp-3) 0 0;
  padding: 0;
  list-style: none;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  align-self: flex-start;
  padding: 1px var(--sp-3);
  border-radius: var(--radius);
  font-size: var(--fs-xs);
  border: var(--bw) solid transparent;
}

.badge.warn {
  background: var(--warn-bg);
  box-shadow: var(--glow-warn);
  border-color: var(--warn-border);
  color: var(--warn);
}

.badge.error {
  background: var(--error-bg);
  box-shadow: var(--glow-error);
  border-color: var(--error-border);
  color: var(--error);
}

.actions {
  flex: none;
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  opacity: 0;
  transition: opacity var(--dnd-dur) var(--dnd-ease);
}

.card:hover .actions,
.card:focus-within .actions {
  opacity: 1;
}

/* Под замком мутирующих кнопок нет — вместо них подпись, объясняющая, почему
   карточка не реагирует. Она стоит в том же ряду действий и раскрывается
   вместе с ним: рядом остаётся проверка цены, поездку та не меняет. */
.lockedNote {
  flex: 0 1 auto;
  align-self: center;
  min-width: 0;
  color: var(--text-3);
  font-size: var(--fs-xs);
}

.card:not(:hover):not(:focus-within) .actions {
  pointer-events: none;
}

.actionBtn {
  padding: var(--sp-1) var(--sp-2);
  font-size: var(--fs-xs);
  line-height: 1;
}

/* Кнопки-иконки: квадратик без подписи, имя живёт в title/aria-label. */
.iconBtn {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Вверх/вниз на десктопе — столбик шириной в один квадратик: кнопки лежат
   друг на друге и вдвоём занимают ту же высоту, что кнопка-иконка рядом. */
.mover {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.moveBtn {
  width: 24px;
  height: 11px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  line-height: 1;
}

@media (prefers-reduced-motion: reduce) {
  .card {
    transition: none;
  }
}

/* ── Мобильный вид (порог продублирован в ui/shell/useIsMobile.ts) ──────── */
@media (max-width: 720px) {
  /* Наведения на телефоне нет: спрятанные под него кнопки правки и удаления
     иначе недостижимы совсем. Заодно снимаем и запрет на клики по ним. */
  .actions {
    opacity: 1;
  }

  .card:not(:hover):not(:focus-within) .actions {
    pointer-events: auto;
  }

  /* Постоянно видимые кнопки встают отдельной строкой под содержимым: сбоку
     они съедали половину карточки, и от названия оставалось многоточие.
     Ряд остаётся однострочным: правка, удаление, вверх/вниз и проверка цены
     умещаются в ширину карточки, а подпись замка ужимается многоточием. */
  .actions {
    width: 100%;
    justify-content: flex-end;
    margin-top: var(--sp-2);
    text-align: right;
  }

  .lockedNote {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Пальцу нужны большие цели: иконки подрастают, а вверх/вниз возвращаются
     в строку — крупными, как и просили только для мобилки. */
  .iconBtn {
    width: 38px;
    height: 32px;
  }

  .mover {
    flex-direction: row;
    gap: var(--sp-2);
  }

  .moveBtn {
    width: 44px;
    height: 32px;
    font-size: var(--fs-md);
  }

  /* Заголовок карточки — одна строка: «LCA → EVN» разорванным по стрелке
     не читается вовсе. Что не влезло, уходит в многоточие. */
  .iataTitle,
  .title {
    /* min-width: 0 обязателен: без него флекс-элемент не ужимается ниже своего
       содержимого и многоточие никогда не появляется. */
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* А вот времени переноситься можно: обрезать его многоточием нельзя. */
  .times {
    flex-wrap: wrap;
    row-gap: 0;
  }

  .card {
    padding: var(--sp-4);
    gap: var(--sp-3);
  }
}
